import {
  type ComponentType,
  type Dispatch,
  type SetStateAction,
  type SVGProps,
  useMemo,
  useState,
} from "react";
import {
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiCreditCard,
  FiDownload,
  FiEye,
  FiMail,
  FiPrinter,
  FiRefreshCw,
  FiSend,
  FiSlash,
  FiTag,
  FiUser,
  FiXCircle,
} from "react-icons/fi";
import AdminSection from "../../../shared/components/AdminSection";
import Button from "../../../shared/components/Button";
import EmptyState from "../../../shared/components/EmptyState";
import InfoTooltip from "../../../shared/components/InfoTooltip";
import PageSizeSelector, {
  resolvePageSizeLimit,
  useStoredPageSize,
} from "../../../shared/components/PageSizeSelector";
import Spinner from "../../../shared/components/Spinner";
import UserAvatar from "../../../shared/components/UserAvatar";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import type {
  AdminAvailabilityRestrictionPayload,
  AdminEventRegistration,
} from "../services/eventRegistrationAdminService";
import AdminAvailabilityRestrictionModal from "./AdminAvailabilityRestrictionModal";
import {
  canShowGeneratePaymentLink,
  formatPaymentStatusLabel,
  formatRegistrationShirtSizes,
  formatRegistrationStatusLabel,
} from "../../events/shared/utils/eventRegistrationFormatters";
import {
  downloadRegistrationListCsv,
  printRegistrationList,
} from "../utils/registrationListExport";

type IconLike = ComponentType<SVGProps<SVGSVGElement>>;

const CheckIcon = FiCheckCircle as unknown as IconLike;
const CalendarIcon = FiCalendar as unknown as IconLike;
const ClockIcon = FiClock as unknown as IconLike;
const CreditCardIcon = FiCreditCard as unknown as IconLike;
const DownloadIcon = FiDownload as unknown as IconLike;
const EyeIcon = FiEye as unknown as IconLike;
const MailIcon = FiMail as unknown as IconLike;
const PrinterIcon = FiPrinter as unknown as IconLike;
const RefreshIcon = FiRefreshCw as unknown as IconLike;
const SendIcon = FiSend as unknown as IconLike;
const SlashIcon = FiSlash as unknown as IconLike;
const TagIcon = FiTag as unknown as IconLike;
const UserIcon = FiUser as unknown as IconLike;
const XIcon = FiXCircle as unknown as IconLike;

type RegistrationStatusFilter =
  | "pending"
  | "confirmed"
  | "canceled"
  | "waitlist"
  | "all";

type PaymentMethod = "pix" | "credit_card" | "debit_card";
type PaymentMethodFilter = PaymentMethod | "missing" | "all";
type RestrictionFilter = "all" | "with" | "without" | "pending" | "confirmed";

type AdminRegistrationsPanelProps = {
  eventRegistrations: AdminEventRegistration[];
  eventRegistrationsLoading: boolean;
  eventRegistrationsStatus: RegistrationStatusFilter;
  isAdmin: boolean;
  onCopyPaymentLink: (
    id: number,
    paymentMethod?: PaymentMethod,
    existingPaymentLink?: string | null,
  ) => Promise<void>;
  onDeleteRegistration: (
    id: number,
    registration?: AdminEventRegistration | null,
  ) => Promise<void>;
  onLoadEventRegistrations: () => Promise<void>;
  onMarkPaidRegistration: (
    id: number,
    registration?: AdminEventRegistration | null,
  ) => Promise<void>;
  onSaveAvailabilityRestriction: (
    id: number,
    payload: AdminAvailabilityRestrictionPayload,
  ) => Promise<{
    impactedCount?: number;
    lockedCount?: number;
  } | void>;
  onRejectRegistration: (
    id: number,
    registration?: AdminEventRegistration | null,
  ) => Promise<void>;
  setEventRegistrationsStatus: Dispatch<
    SetStateAction<RegistrationStatusFilter>
  >;
};

function registrationStatusMeta(status: AdminEventRegistration["status"]) {
  if (status === "confirmed") {
    return {
      className: "admin-registration-status-badge--confirmed",
      icon: <CheckIcon aria-hidden="true" />,
      label: "Confirmada",
    };
  }
  if (status === "waitlist") {
    return {
      className: "admin-registration-status-badge--waitlist",
      icon: <ClockIcon aria-hidden="true" />,
      label: "Lista de espera",
    };
  }
  if (status === "canceled") {
    return {
      className: "admin-registration-status-badge--canceled",
      icon: <XIcon aria-hidden="true" />,
      label: "Cancelada",
    };
  }
  return {
    className: "admin-registration-status-badge--pending",
    icon: <ClockIcon aria-hidden="true" />,
    label: "Pendente",
  };
}

function statusFilterLabel(status: RegistrationStatusFilter) {
  const t = siteTexts.admin.registrationsTab.statusFilters;
  if (status === "pending") return t.pending;
  if (status === "confirmed") return t.confirmed;
  if (status === "waitlist") return t.waitlist;
  if (status === "canceled") return t.canceled;
  return t.all;
}

function paymentMethodLabel(method?: string | null) {
  const normalized = String(method || "")
    .trim()
    .toLowerCase();
  if (normalized === "pix") return "PIX";
  if (normalized === "credit_card") return "Cartão";
  if (normalized === "debit_card") return "Débito";
  if (normalized === "cash" || normalized === "money") return "Dinheiro";
  return "Não informado";
}

function paymentMethodFilterLabel(method: PaymentMethodFilter) {
  if (method === "all") return "Todos";
  if (method === "missing") return "Não informado";
  return paymentMethodLabel(method);
}

function normalizePaymentMethodFilter(method?: string | null) {
  const normalized = String(method || "")
    .trim()
    .toLowerCase();
  if (
    normalized === "pix" ||
    normalized === "credit_card" ||
    normalized === "debit_card"
  ) {
    return normalized;
  }
  return "";
}

function formatCurrencyFromCents(value?: number | null) {
  const cents = Number(value || 0);
  if (!Number.isFinite(cents) || cents <= 0) return "R$ 0,00";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(cents / 100);
}

function registrationValue(row: AdminEventRegistration) {
  return (
    row.finalAmountCents ??
    row.paymentAmountCents ??
    row.originalAmountCents ??
    0
  );
}

function reusablePaymentLink(row: AdminEventRegistration) {
  const link = String(row.paymentLink || "").trim();
  if (!link) return null;
  const linkAmount = Math.max(
    0,
    Math.round(
      Number(row.paymentLinkFinalAmountCents ?? row.paymentLinkAmountCents ?? 0),
    ),
  );
  const currentAmount = Math.max(0, Math.round(Number(registrationValue(row))));
  return linkAmount > 0 && linkAmount === currentAmount ? link : null;
}

function registrationCategory(row: AdminEventRegistration) {
  return (
    String(row.registrationCategoryName || "").trim() ||
    String(row.registrationCategoryKey || "").trim() ||
    "Categoria não informada"
  );
}

function restrictionFilterLabel(filter: RestrictionFilter) {
  if (filter === "with") return "Com restrição";
  if (filter === "without") return "Sem restrição";
  if (filter === "pending") return "Restrição pendente";
  if (filter === "confirmed") return "Restrição confirmada";
  return "Todos";
}

function restrictionLabels(row: AdminEventRegistration) {
  const details = row.availabilityRestriction?.details || [];
  const labels = details
    .map((item) => String(item.label || "").trim())
    .filter(Boolean);
  const fallback = String(row.availabilityRestriction?.label || "").trim();
  return labels.length > 0 ? labels : fallback ? [fallback] : [];
}

function restrictionStatusMeta(row: AdminEventRegistration) {
  const restriction = row.availabilityRestriction;
  if (!restriction?.hasRestriction || restriction.status === "none") {
    return {
      className: "admin-registration-restriction-badge--none",
      label: "Sem restrição",
    };
  }
  const labels = restrictionLabels(row);
  const label = labels.length > 0 ? labels.join(" • ") : "";
  if (restriction.status === "confirmed") {
    return {
      className: "admin-registration-restriction-badge--confirmed",
      label: `Restrição: ${label || "Confirmada"}`,
    };
  }
  return {
    className: "admin-registration-restriction-badge--pending",
    label: `Restrição pendente: ${label || "Aguardando pagamento"}`,
  };
}

function adminAvatarSex(value?: string | null) {
  return value === "male" || value === "female" || value === "other" ? value : "";
}

function eventName(row: AdminEventRegistration) {
  return (
    String(row.challengeName || "").trim() ||
    String(row.challengeType || "").trim() ||
    `Evento ${row.eventId}`
  );
}

export default function AdminRegistrationsPanel({
  eventRegistrations,
  eventRegistrationsLoading,
  eventRegistrationsStatus,
  isAdmin,
  onCopyPaymentLink,
  onDeleteRegistration,
  onLoadEventRegistrations,
  onMarkPaidRegistration,
  onSaveAvailabilityRestriction,
  onRejectRegistration,
  setEventRegistrationsStatus,
}: AdminRegistrationsPanelProps) {
  const t = siteTexts.admin;
  const info = siteTexts.common.info;
  const [expandedRegistrationIds, setExpandedRegistrationIds] = useState<
    Record<number, boolean>
  >({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useStoredPageSize("tnp_page_size_inscricoes");
  const [paymentMethodFilter, setPaymentMethodFilter] =
    useState<PaymentMethodFilter>("all");
  const [restrictionFilter, setRestrictionFilter] =
    useState<RestrictionFilter>("all");
  const [restrictionModalRegistration, setRestrictionModalRegistration] =
    useState<AdminEventRegistration | null>(null);
  const [restrictionSaving, setRestrictionSaving] = useState(false);
  const [paymentLinkLoadingIds, setPaymentLinkLoadingIds] = useState<
    Set<number>
  >(new Set());

  const summaryCards = useMemo(
    () => [
      {
        className: "admin-registration-summary-card--pending",
        icon: <ClockIcon aria-hidden="true" />,
        label: "Pendentes",
        value: eventRegistrations.filter((row) => row.status === "pending")
          .length,
      },
      {
        className: "admin-registration-summary-card--confirmed",
        icon: <CheckIcon aria-hidden="true" />,
        label: "Confirmadas",
        value: eventRegistrations.filter((row) => row.status === "confirmed")
          .length,
      },
      {
        className: "admin-registration-summary-card--waitlist",
        icon: <ClockIcon aria-hidden="true" />,
        label: "Lista de espera",
        value: eventRegistrations.filter((row) => row.status === "waitlist")
          .length,
      },
      {
        className: "admin-registration-summary-card--canceled",
        icon: <XIcon aria-hidden="true" />,
        label: "Canceladas",
        value: eventRegistrations.filter((row) => row.status === "canceled")
          .length,
      },
    ],
    [eventRegistrations],
  );

  const statusFilters: RegistrationStatusFilter[] = [
    "pending",
    "confirmed",
    "waitlist",
    "canceled",
    "all",
  ];

  const filteredRegistrations = useMemo(() => {
    return eventRegistrations.filter((row) => {
      if (!row.paymentRequired) {
        if (paymentMethodFilter !== "all" && paymentMethodFilter !== "missing") {
          return false;
        }
      } else if (paymentMethodFilter !== "all") {
        const method = normalizePaymentMethodFilter(row.paymentMethod);
        if (paymentMethodFilter === "missing" && method) return false;
        if (paymentMethodFilter !== "missing" && method !== paymentMethodFilter) {
          return false;
        }
      }
      const restrictionStatus = row.availabilityRestriction?.status || "none";
      const hasRestriction = Boolean(row.availabilityRestriction?.hasRestriction);
      if (restrictionFilter === "with") return hasRestriction;
      if (restrictionFilter === "without") return !hasRestriction;
      if (restrictionFilter === "pending") return restrictionStatus === "pending";
      if (restrictionFilter === "confirmed") {
        return restrictionStatus === "confirmed";
      }
      return true;
    });
  }, [eventRegistrations, paymentMethodFilter, restrictionFilter]);

  async function handleCopyPaymentLink(
    row: AdminEventRegistration,
    paymentMethod?: PaymentMethod,
  ) {
    const id = Number(row.id || 0);
    if (!id || paymentLinkLoadingIds.has(id)) return;
    setPaymentLinkLoadingIds((current) => new Set(current).add(id));
    try {
      await onCopyPaymentLink(id, paymentMethod, reusablePaymentLink(row));
    } finally {
      setPaymentLinkLoadingIds((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
    }
  }

  function handleExportRegistrations() {
    const exported = downloadRegistrationListCsv({
      registrations: filteredRegistrations,
      title: "lista-de-inscritos",
    });
    if (!exported) {
      window.alert("Nenhuma inscrição disponível para exportar.");
    }
  }

  function handlePrintRegistrations() {
    const printed = printRegistrationList({
      registrations: filteredRegistrations,
      title: "Lista de inscritos",
    });
    if (!printed) {
      window.alert("Não foi possível abrir a impressão da lista de inscritos.");
    }
  }

  const pageLimit = resolvePageSizeLimit(
    pageSize,
    filteredRegistrations.length,
  );
  const pageCount = Math.max(
    1,
    Math.ceil(filteredRegistrations.length / pageLimit),
  );
  const safePage = Math.min(currentPage, pageCount);
  const pagedRegistrations =
    pageSize === "all"
      ? filteredRegistrations
      : filteredRegistrations.slice(
          (safePage - 1) * pageLimit,
          safePage * pageLimit,
        );
  const visibleStart =
    filteredRegistrations.length === 0 ? 0 : (safePage - 1) * pageLimit + 1;
  const visibleEnd = Math.min(
    safePage * pageLimit,
    filteredRegistrations.length,
  );

  const paymentMethodFilters: PaymentMethodFilter[] = [
    "all",
    "pix",
    "credit_card",
    "debit_card",
    "missing",
  ];
  const restrictionFilters: RestrictionFilter[] = [
    "all",
    "with",
    "without",
    "pending",
    "confirmed",
  ];

  return (
    <div className="admin-content">
      {restrictionModalRegistration && (
        <AdminAvailabilityRestrictionModal
          registration={restrictionModalRegistration}
          saving={restrictionSaving}
          canRemove={isAdmin}
          onClose={() => setRestrictionModalRegistration(null)}
          onSubmit={async (payload) => {
            setRestrictionSaving(true);
            try {
              const result = await onSaveAvailabilityRestriction(
                restrictionModalRegistration.id,
                payload,
              );
              if (payload.mode !== "impact") {
                setRestrictionModalRegistration(null);
              }
              return result;
            } finally {
              setRestrictionSaving(false);
            }
          }}
        />
      )}
      <AdminSection className="admin-registrations-dashboard">
        <div className="admin-registrations-hero">
          <div className="admin-registrations-hero-copy">
            <span className="admin-registrations-hero-icon" aria-hidden="true">
              🎟️
            </span>
            <div>
              <h2 className="info-label">
                Central de Inscrições
                <InfoTooltip content={info.registrations} />
              </h2>
              <p>{t.misc.registrationsDescription}</p>
            </div>
          </div>
          <div className="admin-registrations-hero-actions">
            <Button
              type="button"
              variant="pill"
              onClick={handlePrintRegistrations}
              disabled={
                eventRegistrationsLoading || filteredRegistrations.length === 0
              }
            >
              <PrinterIcon aria-hidden="true" />
              Imprimir
            </Button>
            <Button
              type="button"
              variant="pill"
              onClick={handleExportRegistrations}
              disabled={
                eventRegistrationsLoading || filteredRegistrations.length === 0
              }
            >
              <DownloadIcon aria-hidden="true" />
              Exportar CSV
            </Button>
            <Button
              type="button"
              variant="pill"
              onClick={onLoadEventRegistrations}
              disabled={eventRegistrationsLoading}
            >
              <RefreshIcon aria-hidden="true" />
              {t.registrationsTab.refresh}
            </Button>
          </div>
        </div>

        <div className="admin-registration-summary-grid">
          {summaryCards.map((card) => (
            <div
              className={`admin-registration-summary-card ${card.className}`}
              key={card.label}
            >
              <span className="admin-registration-summary-icon">
                {card.icon}
              </span>
              <div>
                <strong>{card.value}</strong>
                <span>{card.label}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="admin-registrations-layout">
          <div className="admin-registration-main-panel">
            <div className="admin-registration-filter-row">
              {statusFilters.map((status) => (
                <button
                  key={status}
                  type="button"
                  className={`admin-registration-filter-pill${
                    eventRegistrationsStatus === status ? " is-active" : ""
                  }`}
                  onClick={() => {
                    setEventRegistrationsStatus(status);
                    setCurrentPage(1);
                  }}
                >
                  {statusFilterLabel(status)}
                </button>
              ))}
            </div>

            <div
              className="admin-registration-filter-row admin-registration-filter-row--payment"
              aria-label="Filtrar por tipo de pagamento"
            >
              {paymentMethodFilters.map((method) => (
                <button
                  key={method}
                  type="button"
                  className={`admin-registration-filter-pill${
                    paymentMethodFilter === method ? " is-active" : ""
                  }`}
                  onClick={() => {
                    setPaymentMethodFilter(method);
                    setCurrentPage(1);
                  }}
                >
                  {paymentMethodFilterLabel(method)}
                </button>
              ))}
            </div>

            <div
              className="admin-registration-filter-row admin-registration-filter-row--restriction"
              aria-label="Filtrar por restrição de disponibilidade"
            >
              {restrictionFilters.map((filter) => (
                <button
                  key={filter}
                  type="button"
                  className={`admin-registration-filter-pill${
                    restrictionFilter === filter ? " is-active" : ""
                  }`}
                  onClick={() => {
                    setRestrictionFilter(filter);
                    setCurrentPage(1);
                  }}
                >
                  {restrictionFilterLabel(filter)}
                </button>
              ))}
            </div>

            {eventRegistrationsLoading && (
              <p className="status-message">
                <Spinner label={t.registrationsTab.loading} />
              </p>
            )}

            {!eventRegistrationsLoading &&
              filteredRegistrations.length === 0 && (
                <EmptyState
                  className="admin-empty-state"
                  icon={<span className="material-symbols-outlined">confirmation_number</span>}
                  title={t.registrationsTab.empty}
                  description="Quando novas inscrições corresponderem aos filtros, elas aparecerão aqui."
                  action={
                    <button
                      className="admin-registration-action admin-registration-action--primary"
                      type="button"
                      onClick={onLoadEventRegistrations}
                    >
                      <RefreshIcon aria-hidden="true" />
                      {t.misc.refreshList}
                    </button>
                  }
                />
              )}

            <div className="admin-registration-card-list">
              {pagedRegistrations.map((row) => {
                const status = registrationStatusMeta(row.status);
                const isExpanded = Boolean(expandedRegistrationIds[row.id]);
                const paymentLabel = row.paymentRequired
                  ? paymentMethodLabel(row.paymentMethod)
                  : t.registrationsTab.notRequired;
                const amountLabel = row.paymentRequired
                  ? formatCurrencyFromCents(registrationValue(row))
                  : t.registrationsTab.notRequired;
                const restrictionMeta = restrictionStatusMeta(row);

                return (
                  <article
                    className={`admin-registration-card admin-registration-card--${row.status}`}
                    key={`registration-${row.id}`}
                  >
                    <div className="admin-registration-event">
                      <strong>{eventName(row)}</strong>
                    </div>

                    {row.registrationType === "fixed_pair" ? (
                      <div className="admin-registration-athlete admin-registration-athlete--pair">
                        <div className="admin-registration-pair-avatars">
                          <UserAvatar
                            avatarUrl={row.userAvatarUrl || ""}
                            className="admin-registration-avatar"
                            name={row.userName}
                            sex={adminAvatarSex(row.userSex)}
                            size={58}
                          />
                          <UserAvatar
                            avatarUrl={row.partnerAvatarUrl || ""}
                            className="admin-registration-avatar"
                            name={row.partnerName || "Parceiro"}
                            sex={adminAvatarSex(row.partnerSex)}
                            size={58}
                          />
                        </div>
                        <div className="admin-registration-pair-content">
                          <span className="admin-registration-pair-name">
                            <strong>{row.userName || "Atleta TNP"}</strong>
                            <strong>
                              {row.partnerName || "Parceiro não informado"}
                            </strong>
                          </span>
                          <p className="info-label">
                            {registrationCategory(row)}
                            <InfoTooltip content={info.registrationCategory} />
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="admin-registration-athlete">
                        <UserAvatar
                          avatarUrl={row.userAvatarUrl || ""}
                          className="admin-registration-avatar"
                          name={row.userName}
                          sex={adminAvatarSex(row.userSex)}
                          size={68}
                        />
                        <div>
                          <h3>{row.userName || "Atleta TNP"}</h3>
                          <p className="info-label">
                            {registrationCategory(row)}
                            <InfoTooltip content={info.registrationCategory} />
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="admin-registration-meta">
                      <span
                        className={`admin-registration-status-badge ${status.className}`}
                      >
                        {status.icon}
                        {status.label}
                      </span>
                      <span
                        className={`admin-registration-restriction-badge ${restrictionMeta.className}`}
                        title={
                          restrictionLabels(row).join("\n") || restrictionMeta.label
                        }
                      >
                        <CalendarIcon aria-hidden="true" />
                        {restrictionMeta.label}
                      </span>
                      <div>
                        <small className="info-label">
                          Pagamento
                          <InfoTooltip content={info.externalPayment} />
                        </small>
                        <strong>{paymentLabel}</strong>
                      </div>
                      <div>
                        <small className="info-label">
                          Valor
                          <InfoTooltip content={info.registrationTotal} />
                        </small>
                        <strong>{amountLabel}</strong>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="admin-registration-detail-panel">
                        <div>
                          <UserIcon aria-hidden="true" />
                          <span>{row.userCpf || "CPF não informado"}</span>
                        </div>
                        <div>
                          <MailIcon aria-hidden="true" />
                          <span>{row.userEmail || "E-mail não informado"}</span>
                        </div>
                        <div>
                          <TagIcon aria-hidden="true" />
                          <span>
                            {formatRegistrationStatusLabel(row.status)} ·{" "}
                            {formatPaymentStatusLabel(row.paymentStatus)}
                          </span>
                        </div>
                        {formatRegistrationShirtSizes(row) && (
                          <div>
                            <TagIcon aria-hidden="true" />
                            <span>{formatRegistrationShirtSizes(row)}</span>
                          </div>
                        )}
                        {row.availabilityRestriction?.hasRestriction && (
                          <div>
                            <CalendarIcon aria-hidden="true" />
                            <span>
                              {(row.availabilityRestriction.details || [])
                                .map(
                                  (item) =>
                                    `${item.label} · ${formatCurrencyFromCents(
                                      item.amountCents || 0,
                                    )} · ${formatPaymentStatusLabel(
                                      item.paymentStatus as any,
                                    )}`,
                                )
                                .join(" | ")}
                            </span>
                          </div>
                        )}
                        {row.paymentLink && (
                          <div>
                            <CreditCardIcon aria-hidden="true" />
                            <a
                              href={row.paymentLink}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {t.registrationsTab.paymentLink}
                            </a>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="admin-registration-actions">
                      {isAdmin &&
                        row.paymentStatus !== "confirmed" &&
                        row.status !== "canceled" && (
                          <button
                            className="admin-registration-action admin-registration-action--primary"
                            type="button"
                            onClick={() => onMarkPaidRegistration(row.id, row)}
                          >
                            <CheckIcon aria-hidden="true" />
                            Confirmar
                          </button>
                        )}
                      {row.status !== "canceled" && (
                        <button
                          className="admin-registration-action admin-registration-action--cancel"
                          type="button"
                          onClick={() => onRejectRegistration(row.id, row)}
                        >
                          <SlashIcon aria-hidden="true" />
                          Cancelar
                        </button>
                      )}
                      {String(row.challengeType || "").toLowerCase() ===
                        "tournament" && (
                        <button
                          className="admin-registration-action"
                          type="button"
                          onClick={() => setRestrictionModalRegistration(row)}
                        >
                          <CalendarIcon aria-hidden="true" />
                          {row.availabilityRestriction?.hasRestriction
                            ? "Editar restrição"
                            : "Adicionar restrição"}
                        </button>
                      )}
                      {canShowGeneratePaymentLink(row) && (
                        <button
                          className="admin-registration-action"
                          type="button"
                          disabled={paymentLinkLoadingIds.has(Number(row.id))}
                          onClick={() =>
                            void handleCopyPaymentLink(
                              row,
                              (normalizePaymentMethodFilter(
                                row.paymentMethod,
                              ) || undefined) as PaymentMethod | undefined,
                            )
                          }
                        >
                          <SendIcon aria-hidden="true" />
                          {paymentLinkLoadingIds.has(Number(row.id))
                            ? "Gerando..."
                            : "Enviar cobrança"}
                        </button>
                      )}
                      <button
                        className="admin-registration-action admin-registration-action--view"
                        type="button"
                        onClick={() =>
                          setExpandedRegistrationIds((prev) => ({
                            ...prev,
                            [row.id]: !prev[row.id],
                          }))
                        }
                      >
                        <EyeIcon aria-hidden="true" />
                        Ver inscrição
                      </button>
                      <button
                        className="admin-registration-action admin-registration-action--danger"
                        type="button"
                        onClick={() => onDeleteRegistration(row.id, row)}
                      >
                        <XIcon aria-hidden="true" />
                        {siteTexts.common.remove}
                      </button>
                      <InfoTooltip content={info.remove} />
                    </div>
                  </article>
                );
              })}
            </div>

            {!eventRegistrationsLoading && filteredRegistrations.length > 0 && (
              <div className="admin-history-list-footer admin-registrations-list-footer">
                <span>
                  {t.historyList.showing} {visibleStart} {t.historyList.rangeTo}{" "}
                  {visibleEnd} {t.historyList.rangeOf}{" "}
                  {filteredRegistrations.length} inscrições
                </span>
                <div
                  className="admin-history-pagination"
                  aria-label={t.historyList.paginationAria}
                >
                  <button
                    type="button"
                    disabled={safePage <= 1}
                    onClick={() =>
                      setCurrentPage((page) => Math.max(1, page - 1))
                    }
                    aria-label={t.historyList.previousPageAria}
                  >
                    ‹
                  </button>
                  {Array.from({ length: Math.min(3, pageCount) }).map(
                    (_, index) => {
                      const page = index + 1;
                      return (
                        <button
                          type="button"
                          key={`admin-registration-page-${page}`}
                          className={safePage === page ? "active" : ""}
                          aria-current={safePage === page ? "page" : undefined}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      );
                    },
                  )}
                  <button
                    type="button"
                    disabled={safePage >= pageCount}
                    onClick={() =>
                      setCurrentPage((page) => Math.min(pageCount, page + 1))
                    }
                    aria-label={t.historyList.nextPageAria}
                  >
                    ›
                  </button>
                </div>
                <PageSizeSelector
                  className="admin-history-page-size"
                  value={pageSize}
                  onChange={(value) => {
                    setPageSize(value);
                    setCurrentPage(1);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </AdminSection>
    </div>
  );
}
